import { useAtomValue, useSetAtom } from "jotai";
import { container, button, innerContainer } from "./search-history.css";
import {
  clearSearchHistoryButtonAtom,
  searchHistoryItemsAtom,
} from "@sunrise/search-history";
import { Button, ButtonProps } from "@/components";
import { useTranslator } from "@sunrise/translator";
import { debouncedQueryAtoms } from "@sunrise/search";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { ReactElement, useEffect, useRef, useState } from "react";
import { isArrowLeftKey, isArrowRightKey } from "@/utils/navigation";
import { useScrollOnFocus } from "@/utils/use-scoll-on-focus";

export function SearchHistory({
  focusKey,
  onHide,
  exitLeft,
}: {
  focusKey: string;
  onHide: () => void;
  exitLeft: () => void;
}) {
  const items = useAtomValue(searchHistoryItemsAtom);
  if (items.length === 0) {
    return null;
  }

  return (
    <SearchHistoryInner
      focusKey={focusKey}
      onHide={onHide}
      exitLeft={exitLeft}
      items={items}
    />
  );
}

/**
 * Shows the items when any are present.
 * Will never pull focus to itself.
 */
function SearchHistoryInner({
  focusKey,
  onHide,
  exitLeft,
  items,
}: {
  focusKey: string;
  onHide: () => void;
  exitLeft: () => void;
  items: string[];
}) {
  const clearButton = useAtomValue(clearSearchHistoryButtonAtom);
  const setSearch = useSetAtom(debouncedQueryAtoms.currentValueAtom);
  const t = useTranslator();

  // Have a reference somewhere of which item is focused.
  const [activeText, setActiveText] = useState<string>();
  const isClearButtonFocused = !activeText;
  const focus = useFocusable({
    focusKey,
    focusable: items.length > 0,
    onArrowPress(direction) {
      if (isArrowRightKey(direction)) {
        if (isClearButtonFocused) {
          return false;
        }

        setActiveText((prev) => {
          const item = items.indexOf(prev ?? "");
          return items[item + 1];
        });
        return false;
      }

      if (isArrowLeftKey(direction)) {
        if (isClearButtonFocused) {
          setActiveText(items[items.length - 1]);
          return false;
        }

        if (activeText === items[0]) {
          exitLeft();
          return false;
        }

        setActiveText((prev) => {
          const item = items.indexOf(prev ?? "");
          return items[item === -1 ? 0 : item - 1];
        });
        return false;
      }

      return true;
    },
    onEnterPress() {
      if (clearButton.visible && isClearButtonFocused) {
        setActiveText(undefined);
        clearButton.action();
        onHide();
        return false;
      }

      if (activeText) {
        setSearch(activeText);
        return false;
      }

      return true;
    },
  });

  const outerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Select the first one whenever the items change.
    setActiveText(items[0]);

    // Scroll into view as well.
    outerRef.current?.scrollTo(0, 0);
  }, [items, outerRef]);

  // Should scroll into view the search history itself when it regains focus.
  useScrollOnFocus({
    enabled: true,
    focused: focus.focused,
    ref: outerRef,
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <div data-testid="search-history" className={container} ref={outerRef}>
      <div className={innerContainer} ref={focus.ref}>
        {items.map((item) => (
          <CarouselButton
            focused={focus.focused && item === activeText}
            shouldScroll={item === activeText}
            key={item}
            variant="light"
            className={button}
            iconLeft="search-small"
            data-testid={`search-history-item-${item}`}
            text={item}
          />
        ))}
        {clearButton.visible && (
          <CarouselButton
            shouldScroll={focus.focused && isClearButtonFocused}
            focused={focus.focused && isClearButtonFocused}
            className={button}
            text={t("search_history_clear")}
          />
        )}
      </div>
    </div>
  );
}

function CarouselButton({
  shouldScroll,
  ...rest
}: ButtonProps & { shouldScroll: boolean }): ReactElement {
  const ref = useRef<HTMLButtonElement>(null);

  useScrollOnFocus({
    enabled: true,
    focused: shouldScroll,
    ref,
  });

  return <Button ref={ref} {...rest} />;
}
